import { StudentRoomManager } from "@/manager/room/student.manager";
import { RoomSessionModel } from "@/models";
import { GLApiStatus, GLError } from "@/models/error.model";
import { HelperModel } from "@/models/helper.model";
import { RoomOptions } from "@/manager/room/base.manager";
import { UserModel } from "@/models/user.model";
import { MutationTree } from "vuex";
import { VideoCallStatus } from "../interface";
import { ClassAction, ClassActionFromValue, StudentRoomState, initState } from "./state";
import { cloneDeep } from "lodash";
import { defineVuexName, MODULES } from "@/store/utils";
import { StudentBadge } from "@/ws/models";

const mutations: MutationTree<StudentRoomState> = {
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_INIT_CLASS_LOADING)](s: StudentRoomState, loading: boolean) {
    s.initClassLoading = loading;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_IS_SESSION_ENDED)](s: StudentRoomState, loading: boolean) {
    s.isSessionEnded = loading;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.LEAVE_ROOM)](state: StudentRoomState, _payload: any) {
    Object.assign(state, initState());
  },
  setError(state: StudentRoomState, payload: GLError | null) {
    state.error = payload;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_API_STATUS)](state: StudentRoomState, payload: GLApiStatus | null) {
    state.apiStatus = payload;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_USER)](state: StudentRoomState, payload: UserModel) {
    state.user = payload;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_ROOM_INFO)](state: StudentRoomState, room: RoomSessionModel) {
    const blockStudentUpdate = room.options?.isActionUpToDate;
    state.idOne = room.oneAndOneDto?.studentId ? room.oneAndOneDto.studentId : "";
    state.idHelperOne = room.helperOneAndOneDto?.studentId ? room.helperOneAndOneDto.studentId : "";
    state.teacher = {
      id: room.teacher.id,
      name: room.teacher.name,
      avatar: state.teacher?.avatar || "",
      videoCallStatus: state.teacher ? state.teacher.videoCallStatus : room.teacher.videoCallStatus,
      disconnectTime: room.teacher.disconnectTime ? Date.now() - room.teacher.disconnectTime : null,
    };
    if (room.helper) {
      state.helper = {
        id: room.helper.id,
        name: room.helper.name,
        avatar: state.helper?.avatar || "",
        videoCallStatus: state.helper ? state.helper.videoCallStatus : room.helper.videoCallStatus,
        isVideoShownByTeacher: room.helper.isVideoShownByTeacher,
      };
    } else {
      state.helper = undefined;
    }
    state.students = [];
    for (const st of room.students) {
      const student = {
        id: st.id,
        name: st.name,
        englishName: st.englishName,
        avatar: "",
        badge: st.badge,
        videoCallStatus: st.videoCallStatus,
        index: state.students.length,
        raisingHand: st.isRaisingHand,
        isPalette: st.isPalette,
      };
      if (st.id === state.user?.id) {
        // No need to up-to-date myself when called from reconnection flow
        if (!blockStudentUpdate) {
          student.index = 999;
          state.student = student;
        }
      } else {
        state.students.push(student);
      }
    }
    state.info = room;
    const role = "audience";
    if (!state.manager) {
      const studentRoomManagerParams: RoomOptions = {
        agora: {
          appId: room.streamInfo.appId,
          webConfig: { mode: "rtc", codec: "vp8", role: role },
          user: {
            channel: room.streamInfo.chanelId,
            username: room.streamInfo.userId,
            token: room.streamInfo.token,
            role,
          },
        },
      };
      state.manager = new StudentRoomManager(studentRoomManagerParams);
    }
    state.classAction = ClassActionFromValue(room.lessonPlan.lessonAction);
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.CLEAR_CIRCLE_STATUS)](state: StudentRoomState, payload: { id: string }) {
    const student = payload.id === state.student?.id ? state.student : state.students.find((st) => st.id === payload.id);
    if (student) {
      student.raisingHand = false;
      student.isPalette = false;
    }
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENTS_BADGE)](state: StudentRoomState, payload: StudentBadge[]) {
    payload.forEach((item) => {
      const student = item.studentId === state.student?.id ? state.student : state.students.find((st) => st.id === item.studentId);
      if (student) student.badge = item.badge;
    });
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_PALETTE)](
    state: StudentRoomState,
    payload: { studentId: string; isPaletteEnabled: boolean },
  ) {
    // clear all palette status
    if (payload.isPaletteEnabled) {
      state.students.forEach((student) => {
        student.isPalette = false;
      });
    }
    // set palette status for student
    const student = payload.studentId === state.student?.id ? state.student : state.students.find((st) => st.id === payload.studentId);
    if (student) student.isPalette = payload.isPaletteEnabled;
  },
  disableAllAnnotationStatus(state: StudentRoomState, p: any) {
    state.student ? (state.student.isPalette = !p) : null;
    state.students.forEach((student) => (student.isPalette = !p));
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_CLASS_ACTION)](state: StudentRoomState, payload: { action: ClassAction }) {
    state.classAction = payload.action;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_IS_CONNECTED)](state: StudentRoomState, isConnected: boolean): void {
    state.isConnected = isConnected;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID)](s: StudentRoomState, p: { id: string; withTeacher: boolean }) {
    if (p.withTeacher) s.idOne = p.id;
    else s.idHelperOne = p.id;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_SPEAKING_USERS)](state: StudentRoomState, payload: { userIds: Array<string> }) {
    state.speakingUsers = payload.userIds;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_RAISING_HAND)](
    state: StudentRoomState,
    payload: { studentId: string; raisingHand: boolean },
  ) {
    if (!payload.studentId && state.student?.id) {
      payload.studentId = state.student.id;
    }
    const student = payload.studentId === state.student?.id ? state.student : state.students.find((st) => st.id === payload.studentId);
    if (student) student.raisingHand = payload.raisingHand;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_WHITEBOARD)](state: StudentRoomState, payload: boolean) {
    state.showWhiteBoard = payload;
  },
  setMediaState(state: StudentRoomState, payload: any) {
    state.mediaState = payload;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_ONLINE)](state: StudentRoomState) {
    state.isDisconnected = false;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_OFFLINE)](state: StudentRoomState) {
    state.isDisconnected = true;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_IS_JOINED)](state: StudentRoomState, p: { isJoined: boolean }) {
    state.isJoined = p.isJoined;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_TEACHER_DISCONNECTED)](state: StudentRoomState, p: boolean) {
    state.teacherIsDisconnected = p;
    if (!p && state.teacher) {
      state.teacher.disconnectTime = null;
    }
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_AVATAR_TEACHER)](state: StudentRoomState, p: string) {
    if (state.teacher) state.teacher.avatar = p;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_AVATAR_HELPER)](state: StudentRoomState, p: string) {
    if (state.helper) state.helper.avatar = p;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.TOGGLE_VIDEOS_FEED)](state: StudentRoomState) {
    state.videosFeedVisible = !state.videosFeedVisible;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_HELPER_INFO)](s: StudentRoomState, p: HelperModel | undefined) {
    if (!p) {
      s.helper = p;
      return;
    }
    s.helper = {
      id: p.id,
      name: p.name,
      avatar: s.helper?.avatar || "",
      videoCallStatus: p.videoCallStatus,
      isVideoShownByTeacher: p.isVideoShownByTeacher,
    };
  },
  setHelperCallingConnectionStatus(s: StudentRoomState, p: VideoCallStatus) {
    if (s.helper) {
      s.helper.videoCallStatus = p;
    }
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.SET_HELPER_VIDEO_STATUS)](s: StudentRoomState, p: boolean) {
    if (s.helper) {
      s.helper.isVideoShownByTeacher = p;
    }
  },
  [defineVuexName(MODULES.STUDENT_ROOM.COMMITS.MAKE_HELPER_THE_NEW_TEACHER)](s: StudentRoomState) {
    if (s.helper) {
      s.teacher = {
        ...cloneDeep(s.helper),
      };
      s.helper = undefined;
    }
  },
};

export default mutations;
